// src/admin/FaqsList.jsx
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import FaqEditor from "./FaqEditor";

export default function FaqsList({
  baseURL = "https://gantasoft.in/rankridgeschool/dev/v1",
  apiKey = "12345678",
  pageId,
  subpageId,
  path, // optional: load FAQs by URL path (e.g., "/academics")
}) {
  const api = useMemo(
    () =>
      axios.create({
        baseURL,
        headers: { "X-API-Key": apiKey },
        timeout: 20000,
      }),
    [baseURL, apiKey]
  );

  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editorItem, setEditorItem] = useState(null);

  const buildParams = () => {
    const p = { sort: "sort_order", order: "ASC", page, limit };
    if (q.trim()) p.q = q.trim();
    if (status) p.status = status;
    if (pageId) p.page_id = pageId;
    if (subpageId) p.subpage_id = subpageId;
    if (path) p.path = path;
    return p;
  };

  async function load() {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/faqs", { params: buildParams() });
      setRows(data?.items || []);
      setTotal(data?.pagination?.total || 0);
      setPages(data?.pagination?.pages || 0);
    } catch (e) {
      setError(
        e?.response?.data?.debug || e?.response?.data?.message || e.message
      );
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, status, page, pageId, subpageId, path]);

  const patch = async (id, field, value) => {
    await api.patch(`/faqs/${id}`, { field, value });
    setRows((arr) =>
      arr.map((r) => (r.faq_id === id ? { ...r, [field]: value } : r))
    );
  };

  const toggleStatus = async (it) => {
    const next =
      it.status === "active" ? "inactive" : it.status === "inactive" ? "active" : "active";
    try {
      await patch(it.faq_id, "status", next);
    } catch (e) {
      alert(e?.response?.data?.message || e.message || "Status change failed");
    }
  };

  const destroy = async (it) => {
    if (!window.confirm("Delete this FAQ?")) return;
    try {
      await api.delete(`/faqs/${it.faq_id}`);
      setRows((arr) => arr.filter((r) => r.faq_id !== it.faq_id));
    } catch (e) {
      alert(e?.response?.data?.message || e.message || "Delete failed");
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-4">
      <div className="flex flex-wrap items-end gap-2 justify-between">
        <h2 className="text-xl font-semibold">FAQs</h2>
        <div className="flex gap-2">
          <button onClick={load} className="px-3 py-1.5 bg-gray-200 rounded">
            Refresh
          </button>
          <button
            onClick={() =>
              setEditorItem({
                faq_id: null,
                page_id: pageId || null,
                subpage_id: subpageId || null,
                question: "",
                answer: "",
                sort_order: 0,
                status: "active",
              })
            }
            className="px-3 py-1.5 bg-green-700 text-white rounded"
          >
            + Add FAQ
          </button>
        </div>
      </div>

      <div className="mt-4 grid md:grid-cols-4 gap-2">
        <input
          placeholder="Search question/answer…"
          className="border rounded px-3 py-2"
          value={q}
          onChange={(e) => {
            setPage(1);
            setQ(e.target.value);
          }}
        />
        <select
          className="border rounded px-3 py-2"
          value={status}
          onChange={(e) => {
            setPage(1);
            setStatus(e.target.value);
          }}
        >
          <option value="">All statuses</option>
          <option value="active">active</option>
          <option value="inactive">inactive</option>
          <option value="deleted">deleted</option>
        </select>
      </div>

      {error && (
        <div className="mt-3 px-3 py-2 text-sm rounded bg-red-50 text-red-700 border border-red-200">
          {error}
        </div>
      )}

      {loading ? (
        <div className="mt-4 text-gray-500">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="mt-4 text-gray-500">No results.</div>
      ) : (
        <div className="mt-4 grid gap-3">
          {rows.map((it) => (
            <div key={it.faq_id} className="border rounded p-3 bg-white">
              <div className="flex items-center justify-between">
                <div className="font-medium">{it.question}</div>
                <div className="flex items-center gap-2">
                  <span className="text-xs px-2 py-0.5 rounded bg-gray-100">
                    {it.status}
                  </span>
                  <span className="text-xs px-2 py-0.5 rounded bg-gray-100">
                    Sort:{" "}
                    <input
                      type="number"
                      className="w-16 text-right border rounded px-1 py-0.5"
                      value={it.sort_order ?? 0}
                      onChange={(e) => {
                        const val = Number(e.target.value || 0);
                        setRows((arr) =>
                          arr.map((r) =>
                            r.faq_id === it.faq_id
                              ? { ...r, sort_order: val }
                              : r
                          )
                        );
                      }}
                      onBlur={async (e) => {
                        const val = Number(e.target.value || 0);
                        try { await patch(it.faq_id, "sort_order", val); } catch {}
                      }}
                    />
                  </span>
                </div>
              </div>

              {it.answer && (
                <div className="text-sm text-gray-700 mt-2 whitespace-pre-wrap">
                  {it.answer}
                </div>
              )}

              <div className="text-xs text-gray-500 mt-2">
                ID: {it.faq_id} · Page: {it.page_id ?? "-"} · Subpage:{" "}
                {it.subpage_id ?? "-"}
              </div>

              <div className="flex gap-2 mt-2">
                <button
                  onClick={() => toggleStatus(it)}
                  className="px-2 py-1 text-xs rounded bg-gray-800 text-white"
                >
                  {it.status === "active" ? "Deactivate" : "Activate"}
                </button>
                <button
                  onClick={() => setEditorItem(it)}
                  className="px-2 py-1 text-xs rounded bg-blue-600 text-white"
                >
                  Edit
                </button>
                <button
                  onClick={() => destroy(it)}
                  className="px-2 py-1 text-xs rounded bg-red-600 text-white"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {pages > 1 && (
        <div className="mt-4 flex items-center gap-2">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="px-3 py-1.5 rounded bg-gray-200 disabled:opacity-50"
          >
            Prev
          </button>
          <div className="text-sm">
            Page {page} of {pages} · {total} total
          </div>
          <button
            onClick={() => setPage((p) => Math.min(pages, p + 1))}
            disabled={page >= pages}
            className="px-3 py-1.5 rounded bg-gray-200 disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}

      {editorItem && (
        <FaqEditor
          baseURL={baseURL}
          apiKey={apiKey}
          initial={editorItem}
          onClose={() => setEditorItem(null)}
          onSaved={() => {
            setEditorItem(null);
            load();
          }}
        />
      )}
    </div>
  );
}
